import { useEffect } from 'react';

/**
 * Issue #78: Lock background (page) scrolling while a modal/popover is open.
 *
 * When an overlay popover is displayed, the underlying page must not move,
 * scroll or be interacted with via scroll/touch gestures. This hook freezes
 * the document body while `active` is true and restores the previous scroll
 * behaviour (including the exact scroll position) on cleanup.
 *
 * Shared hook so every popover/modal can lock the background the same way.
 *
 * @param {boolean} active - Whether the scroll lock should be applied.
 */
const useBodyScrollLock = (active = true) => {
  useEffect(() => {
    if (!active) {
      return undefined;
    }

    const { body } = document;
    const scrollY = window.scrollY;

    // Preserve styles we are about to override so we can restore them exactly.
    const previous = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
    };

    // Fixing the body in place prevents the background from scrolling on both
    // desktop (wheel) and touch devices, where `overflow: hidden` alone is not
    // always sufficient.
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';

    return () => {
      body.style.overflow = previous.overflow;
      body.style.position = previous.position;
      body.style.top = previous.top;
      body.style.width = previous.width;
      // Restore the scroll position the user was at before the lock.
      window.scrollTo(0, scrollY);
    };
  }, [active]);
};

export default useBodyScrollLock;
